import { useRef, useState } from "react";
import type { Country } from "@shared/types.js";
import { api } from "../api.js";

/** Upload a Masterfile; the server parses it and stores a new snapshot. */
export function UploadModal({
  onClose, onDone, country,
}: {
  country: Country;
  onClose: () => void;
  onDone: (id: number, notes: string[]) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const pick = (f: File | null | undefined) => {
    if (!f) return;
    if (!/\.(xlsx|xlsm)$/i.test(f.name)) {
      setErr("Only .xlsx or .xlsm files can be parsed.");
      return;
    }
    setErr("");
    setFile(f);
  };

  const submit = async () => {
    if (!file || busy) return;
    setBusy(true);
    setErr("");
    try {
      const res = await api.upload(file, label, country);
      onDone(res.meta.id, res.notes ?? []);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <div className="modal-back" onClick={onClose} role="presentation">
      <div
        className="modal" role="dialog" aria-modal="true" aria-label="Upload Masterfile"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mhead"><h3><i className="bi bi-upload hico" aria-hidden="true" />Upload Masterfile</h3></div>
        <div className="mbody">
          <div
            className={`drop${over ? " over" : ""}`}
            onClick={() => input.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setOver(true); }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setOver(false);
              pick(e.dataTransfer.files[0]);
            }}
            role="button" tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") input.current?.click(); }}
          >
            <b>{file ? file.name : "Choose or drop an .xlsx file"}</b>
            {file
              ? `${(file.size / 1024 / 1024).toFixed(1)} MB — click to pick a different file`
              : "Sheet and column names must match the field mapping"}
          </div>
          <input
            ref={input} type="file" accept=".xlsx,.xlsm" hidden
            onChange={(e) => pick(e.target.files?.[0])}
          />

          <div className="field">
            <label htmlFor="snaplabel">Label (optional)</label>
            <input
              id="snaplabel" type="text" placeholder="e.g. Week 38 — after Bali batch"
              value={label} onChange={(e) => setLabel(e.target.value)}
            />
          </div>

          {err && <div className="err">{err}</div>}

          <p style={{ margin: 0, fontSize: 11.5, color: "var(--ink-3)" }}>
            The workbook is parsed on the server and stored as a new snapshot. Earlier snapshots
            are kept, so the Progress tab can chart movement between uploads.
          </p>
        </div>
        <div className="mfoot">
          <button className="btn" type="button" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn--primary" type="button" onClick={() => void submit()} disabled={!file || busy}>
            {busy ? <span className="rowflex"><span className="spinner" /> Parsing…</span> : "Upload & parse"}
          </button>
        </div>
      </div>
    </div>
  );
}
